<template>
  <div
    id="player"
    class="c-player u-color-invert"
    :class="{
      'is-active': isActive,
      'c-player--embedded': isEmbedded,
    }"
    @mousemove="handleMouseMove"
  >
    <div class="c-player__wrapper js-player-wrapper">
      <v-product-image
        v-if="product && (isWaitingInteraction || isCountdownVisible || isAutoplayBlocked)"
        :product="product.currentProgram || product"
        :type="ImageType.ARTWORK"
        class="c-player__image"
      ></v-product-image>
      <!-- Android Webview requires poster image, otherwise throws error destroying player -->
      <video
        v-if="videoTagVisible"
        :id="productId"
        ref="video"
        class="js-player-video"
        poster="/static/images/fake-android-webview-poster.gif"
        playsinline
      ></video>
      <ui-player-overlay :is-embedded="isEmbedded"></ui-player-overlay>
      <div class="c-player__popup-container">
        <ui-adblock-overlay v-if="$store.getters.adblockDetected"></ui-adblock-overlay>
        <ui-countdown
          v-else-if="isCountdownVisible"
          :available-since="availableSince"
          :is-programme-disabled="isProgrammeDisabled"
          @countdownStop="hideCountdownAndStartPlayer"
        ></ui-countdown>
        <ui-catchup-unavailable v-else-if="isCatchupUnavailable"></ui-catchup-unavailable>
        <ui-content-prohibited
          v-else-if="isContentProhibitedVisible"
          :is-embedded="isEmbedded"
          :is-logged-in-underage="isLoggedInUnderage"
        ></ui-content-prohibited>
        <ui-verify-age
          v-else-if="isVerifyAgeVisible"
          :is-embedded="isEmbedded"
          @confirmed="AgeConfirmed"
          @rejected="AgeRejected"
        ></ui-verify-age>
        <ui-content-disabled
          v-else-if="isProgrammeDisabled"
          :is-embedded="isEmbedded"
          :product="product"
        ></ui-content-disabled>
      </div>
      <slot name="logos"></slot>
      <ui-error ref="alert-generic"></ui-error>
      <ui-freewheel
        v-if="areAdsEnabled"
        :is-advert-player-active="isAdvertPlayerActive"
        :free-wheel-player="freeWheelPlayer"
        :product="product"
        :is-embedded="isEmbedded"
      ></ui-freewheel>
      <ui-player-controls
        :is-embedded="isEmbedded"
        :visible="!isAdvertPlayerActive && controlsVisible"
      ></ui-player-controls>
      <ui-coming-next
        v-if="comingNextVisible"
        :message="comingNextMessage"
        :product="comingNextProduct"
        @playAgain="playAgain"
        @playNext="playNext"
      ></ui-coming-next>
    </div>
  </div>
</template>
<script>
  import axios from 'axios';
  import { SDK } from '@redge-media/fo-js-sdk';
  import url from 'url';
  import dayjs from 'dayjs';
  import { BookmarkVideoType, VideoType } from '@/shared/libs/player';
  import { ADULT_RATING_AGE, getActiveScheduleByType, getCurrentProgramByPriority, getMainCategoryLabel, isUnderage } from '@/shared/utils';
  import formatTime from '@/shared/utils/time';
  import { DisplayScheduleType, EmbeddedRouteName, ErrorCode, HeaderName, ImageType, ProductType } from '@/shared/enums';
  import { addFullscreenListener, fullscreenChange, removeFullscreenListener } from '@/shared/utils/full-screen';
  import UiAdblockOverlay from '@/shared/components/player/components/adblock-overlay/index.vue';
  import UiPlayerControls from '@/shared/components/player/components/controls/index.vue';
  import UiPlayerOverlay from '@/shared/components/player/components/overlay/index.vue';
  import UiCountdown from '@/shared/components/player/components/countdown/index.vue';
  import UiCatchupUnavailable from '@/shared/components/player/components/catchupUnavailable/index.vue';
  import UiComingNext from '@/shared/components/player/components/coming-next/index.vue';
  import {
    MAX_VOLUME,
    MIN_VOLUME,
    addEventOnTimeline,
    clearPlayerStatus,
    setAdvertControlsEnabled,
    setAutoplayStatus,
    setControlsEnabled,
    setCurrentLiveProgram,
    setDefaultPlayerSettings,
    setDuration,
    setIdle,
    setInitState,
    setMuted,
    setOverlayStatus,
    setPlayerStatus,
    setProduct,
    setProgress,
    setSeekTo,
    setSpriteBaseUrl,
    setSpriteManifest,
    setTimeshiftMode,
    state,
  } from '@/shared/components/player/store';
  import { AutoplayStatus, OverlayStatus, PlayerStatus, SubtitlesLanguage, TimelineEventAction, TimeshiftMode } from '@/shared/components/player/enums';
  import advertType from '@/shared/enums/advert-type';
  import { isObjectEmpty } from '@/shared/libs/helpers';
  import { getDeviceType } from '@/shared/plugins/freewheel/helpers';
  import { getLocation } from '@/shared/plugins/stats/helpers';
  import VProductImage from '@/index/shared/components/product-image/index.vue';
  import { link } from '@/index/shared/filters';
  import { embeddedProductRouterMixin, productMixin } from '@/index/shared/mixins';
  import { ProductRecommendations } from '@/shared/libs/sdk';
  import { useMediaTypes } from './composables';
  import {
    NextEpisodeMixin,
    NextProgramMixin,
    NextRelatedProductMixin,
    PlayerEventsMixin,
    StartOverMixin,
    verifyAgeMixin,
  } from './mixins';
  import { UiContentDisabled, UiContentProhibited, UiError, UiFreewheel, UiVerifyAge } from './components';
  import { getTrackLanguage } from './store-utils';

  const ERROR_INTERRUPT_TIME = 3600; // [s]
  const DEFAULT_DRM_SYSTEMS = ['fairplay', 'widevine', 'playready'];

  const rewriteUrl = (src) => {
    if (process.env.NODE_ENV === 'development') {
      return url.format({
        ...url.parse(src),
        host: window.location.host,
        protocol: window.location.protocol === 'https:' ? 'https' : 'http',
      });
    }

    return src;
  };

  export default {
    components: {
      UiPlayerOverlay,
      UiError,
      UiAdblockOverlay,
      UiPlayerControls,
      UiFreewheel,
      UiVerifyAge,
      VProductImage,
      UiContentProhibited,
      UiCountdown,
      UiCatchupUnavailable,
      UiComingNext,
      UiContentDisabled,
    },
    mixins: [
      NextEpisodeMixin,
      NextRelatedProductMixin,
      NextProgramMixin,
      PlayerEventsMixin,
      StartOverMixin,
      verifyAgeMixin,
      embeddedProductRouterMixin,
      productMixin,
    ],
    provide() {
      return {
        $player: this,
      };
    },
    props: {
      autoplay: {
        type: Boolean,
        default: true,
      },
      autoplayOnHover: Boolean,
      muted: Boolean,
      isEmbedded: {
        type: Boolean,
      },
    },
    setup() {
      const { getPreferredMediaTypes, hevcPlayback, originalPlaylist } = useMediaTypes();

      return {
        hevcPlayback,
        originalPlaylist,
        getPreferredMediaTypes,
      };
    },
    data() {
      return {
        durationExceeded: false,
        videoTagVisible: true,
        bookmarks: this.$services.resolve('bookmarks'),
        info: this.$services.resolve('info'),
        subscriber: this.$services.resolve('subscriber'),
        liveManager: this.$services.resolve('liveManager'),
        time: this.$services.resolve('time'),
        bookmark: 0,
        lastSessionError: 0,
        product: null,
        videoType: null,
        mediumType: null,
        player: null,
        freeWheelPlayer: null,
        playPromise: null,
        savedPosition: 0,
        saveBookmarkAfterAds: false,
        config: {},
        state,
        isVerifyAgeVisible: false,
        verifyAgeResponses: null,
        isContentProhibitedVisible: false,
        ImageType,
        isCountdownVisible: false,
        availableSince: null,
        isCatchupUnavailable: null,
        comingNextVisible: false,
        comingNextProduct: null,
        comingNextMessage: '',
        abortController: undefined,
      };
    },
    computed: {
      isActive() {
        return state.overlayStatus === OverlayStatus.INTERACTION;
      },
      isAdvertPlayerActive() {
        return [PlayerStatus.ADVERTS_BUFFERING, PlayerStatus.ADVERTS_PAUSE, PlayerStatus.ADVERTS_PLAYING].includes(state.playerStatus);
      },
      isWaitingInteraction() {
        return state.playerStatus === PlayerStatus.WAITING_INTERACTION;
      },
      isAutoplayBlocked() {
        return state.autoplayStatus === AutoplayStatus.BLOCKED;
      },
      isLoggedInUnderage() {
        return isUnderage(this.subscriber.dateOfBirth);
      },
      isPopup() {
        return this.isCountdownVisible || this.isCatchupUnavailable || this.isContentProhibitedVisible || this.isVerifyAgeVisible || this.isProgrammeDisabled;
      },
      controlsVisible() {
        return !state.isLinearAdPlaying && !this.isPopup && !this.comingNextVisible;
      },
      noAdsOnEmbedded() {
        return state.isEmbedded && this.$route.query.CustomVariable1 === 'klik';
      },
      productId() {
        return this.product?.id;
      },
      recommendationAutoplay() {
        return state.isEmbedded && this.$route.query.recommendationAutoplay === 'true';
      },
      areAdsEnabled() {
        return !this.noAdsOnEmbedded && !this.subscriber.isNoAds;
      },
      isFollowingElement() {
        return state.isEmbedded && this.$route.params.isFollowingElement;
      },
    },
    watch: {
      isPopup(value) {
        state.isPopupVisible = value;
      },
      '$store.getters.adblockDetected': function (value) {
        if (value) {
          this.getPlayPromise().finally(() => this.close());
        } else {
          this.getPlayPromise().then(() => this.start());
        }
      },
      '$store.getters.atdsTrackingBlocked': function (value) {
        if (value) {
          this.onTrackingError();
        } else {
          this.getPlayPromise().then(() => this.start());
        }
      },
      'state.isSeeking': function (value) {
        if (!value) {
          return;
        }

        this.$stats.seek(state.product, this.getStatsCurrentTime());
      },
      'state.settings.audio': function (audio, oldAudio) {
        const audioLang = getTrackLanguage(audio);
        const oldAudioLang = getTrackLanguage(oldAudio);

        if (audio.id === oldAudio.id && audioLang === oldAudioLang) {
          return;
        }

        this.player.switchAudioTrack(audio.id);
      },
      'state.settings.profile': function (profile, oldProfile) {
        if (profile.bandwidth === oldProfile.bandwidth) {
          return;
        }

        const quality = profile.height || profile.name;
        this.$stats.changePlayingSettings(quality, state.product);

        this.player.switchProfile(profile.bandwidth);
      },
      'state.settings.subtitle': function (subtitle) {
        if (subtitle.language === SubtitlesLanguage.DISABLED) {
          this.player.disableSubtitle();
        } else {
          this.player.setActiveSubtitle(subtitle);
        }
      },
      'state.volume': function () {
        this.updateVolume();
      },
      'state.playerStatus': function (status, prevStatus) {
        const player = this.player;

        if (!player) {
          // to prevent early errors
          return;
        }

        if (status === prevStatus) {
          return;
        }

        if (status === PlayerStatus.PAUSE) {
          player.pause();
          return setTimeshiftMode(TimeshiftMode.LIVE_DELAYED);
        }

        if (status === PlayerStatus.PLAYING) {
          return player.play();
        }
      },
      'state.seekTo': function (seek) {
        if (seek === null) {
          return;
        }

        const player = this.player;

        if (!player) {
          return;
        }

        if (state.product.type === ProductType.LIVE) {
          const timeShiftStart = player.timeShift.getStart();
          const currentLiveStart = state.currentLiveProgram.since / 1000;
          const timeShiftOffset = currentLiveStart - timeShiftStart;
          const moveTo = player.getCurrentTime() - seek;

          if (moveTo < 0) {
            return;
          }

          if (moveTo - timeShiftOffset >= state.head) {
            setProgress(state.head);
            setTimeshiftMode(TimeshiftMode.LIVE);
            return player.head();
          }

          setProgress(moveTo - timeShiftOffset);
          setTimeshiftMode(TimeshiftMode.LIVE_DELAYED);
          setSeekTo(null);
          return player.seekTo(moveTo);
        }

        player.seekTo(seek);
        setProgress(seek);
        setSeekTo(null);
      },
      'state.mute': function () {
        this.updateVolume();
      },
      'state.timeshiftMode': function (mode) {
        const player = this.player;
        const timeShift = player && player.timeShift;
        if (timeShift && timeShift.isEnabled() && mode === TimeshiftMode.LIVE) {
          setProgress(state.head);
          player.head();
        }
      },
      'state.currentLiveProgram': function (program = {}) {
        if (this.isProgrammeDisabled) {
          this.player?.stop();
          clearPlayerStatus();
        }

        const till = program.tillDate;
        if (!till) {
          return;
        }
        setDuration(program.tillDate.diff(program.since, 's'));
      },
    },
    beforeCreate() {
      setDefaultPlayerSettings();
    },
    mounted() {
      addFullscreenListener(this.fullscreenChange);
      window.addEventListener('unload', this.close);
    },
    beforeDestroy() {
      removeFullscreenListener(this.fullscreenChange);
      window.removeEventListener('unload', this.close);

      this.close();
    },
    methods: {
      load({ product, videoType, catchupUnavailable, mediumType = 'video' }) {
        this.product = product;
        this.videoType = videoType;
        this.mediumType = mediumType;
        this.id = this.product.id;

        this.$store.dispatch('adblock/check');
        this.$store.dispatch('atdsTracking/check');

        this.availableSince = this.getAvailableSince();

        if (this.availableSince) {
          this.isCountdownVisible = true;
          return;
        }

        if (catchupUnavailable) {
          this.isCatchupUnavailable = true;
          return;
        }

        this.start();
      },
      playAgain() {
        this.comingNextVisible = false;
        this.isEmbedded ? this.play() : this.load(this);
      },
      playNext() {
        this.comingNextVisible = false;
        this.isEmbedded ? this.updateEmbeddedRoute(this.comingNextProduct) : this.$router.push(link(this.comingNextProduct));
      },
      async setProduct() {
        const { product, videoType } = this;
        if (this.videoType === VideoType.RECORDING
          || this.videoType === VideoType.CATCHUP) {
          this.id = this.product.programRecordingId;
        }

        if (videoType === VideoType.CATCHUP) {
          const channel = this.liveManager.channels.get(product.channelId);
          if (channel?.previewUrl) {
            this.product.previewUrl = channel.previewUrl;
          }
        }

        const currentProgram = getCurrentProgramByPriority({ product: this.product, currentTime: this.time.now });
        setCurrentLiveProgram(currentProgram);
        await setProduct(this.product);
      },
      start() {
        if (this.$store.getters.adblockDetected) {
          return;
        }

        if (this.$store.getters.atdsTrackingBlocked) {
          return this.onTrackingError();
        }

        if (this.isLoggedInUnderage && this.product.rating >= ADULT_RATING_AGE) {
          this.isContentProhibitedVisible = true;
          return;
        }

        this.verifyAgePromise()
          .then(() => {
            if (this.isEmbedded) {
              setMuted(this.muted);
            }

            this.$chartbeat.load(state);

            if (this.autoplay || this.isFollowingElement) {
              return this.play();
            }

            setPlayerStatus(PlayerStatus.WAITING_INTERACTION);
            return this.refreshVideoTag()
              .then(async () => {
                await this.setProduct();
                if (state.product.type !== ProductType.LIVE) {
                  setDuration(this.product.duration);
                }
              });
          })
          .catch(() => {
            this.close();
            this.isContentProhibitedVisible = true;
          });
      },
      play() {
        this.hideAlert();

        this.playPromise = this.refreshVideoTag()
          .then(async () => {
            setPlayerStatus(PlayerStatus.LOADING);
            this.abortController = new AbortController();

            const { product, videoType, mediumType } = this;

            await this.setProduct();

            if (this.isProgrammeDisabled) {
              clearPlayerStatus();
              return Promise.resolve();
            }

            const config = await this.loadConfig({ product, videoType, mediumType });
            const playlist = await this.loadPlaylist({ config, product, videoType, mediumType });

            if (isObjectEmpty(playlist.file)) {
              return Promise.reject();
            }

            return this.checkDashManifest(playlist)
              .then(() => this.setup({ product, videoType, config, playlist: this.getPreferredMediaTypes(playlist, product) }));
          })
          .catch((resp = {}) => {
            const code = resp.response && resp.response.data && resp.response.data.code;
            const message = resp.message && resp.message.replace(/\s/g, '_').toUpperCase();

            setPlayerStatus(PlayerStatus.ERROR);
            setControlsEnabled(false);
            this.showAlert(code || message);
          });
      },
      refreshVideoTag() {
        if (!this.player) {
          return Promise.resolve();
        }

        this.destroy();
        this.videoTagVisible = false;

        return this.$nextTick()
          .then(() => {
            this.videoTagVisible = true;
            return this.$nextTick();
          });
      },
      loadConfig({ videoType, mediumType }) {
        const headers = this.getConfigurationHeaders();
        return SDK.apiAdapter.get(`/products/${this.id}/${mediumType}s/player/configuration`, { videoType, dai: true }, { headers })
          .then(({ data }) => {
            const { displayRating, playlistUrl, timeshift, videoSession = {}, tracking = undefined } = data;
            const { introFinishTime, introStartTime, creditsTime } = data;
            if (introStartTime) {
              addEventOnTimeline({
                title: this.$t('player.skip'),
                since: introStartTime,
                till: introFinishTime,
                action: TimelineEventAction.SKIP_INTRO,
              });
            }
            if (!this.isEmbedded && (state.product.type === ProductType.EPISODE && creditsTime)) {
              addEventOnTimeline({
                title: this.$t('player.credits'),
                since: creditsTime,
                action: TimelineEventAction.WATCH_CREDITS,
              });

              if (this.nextEpisode.id) {
                addEventOnTimeline({
                  title: this.$t('player.next'),
                  since: creditsTime,
                  action: TimelineEventAction.NEXT_EPISODE,
                });
              }
            }
            return {
              videoSessionId: videoSession.videoSessionId,
              prolongVideoSessionUrl: videoSession.prolongUrl && rewriteUrl(videoSession.prolongUrl.split('?')[0]),
              prolongVideoSessionInterval: videoSession.prolongInterval,
              playlistUrl: playlistUrl && rewriteUrl(playlistUrl.split('?')[0]),
              displayRating,
              timeShift: timeshift,
              tracking: this.getTracking({ tracking }),
              creditsTime,
            };
          });
      },
      loadPlaylist({ config, videoType, mediumType }) {
        const playlistUrl = config.playlistUrl || `/products/${this.id}/${mediumType}s/playlist`;

        return SDK.apiAdapter.get(playlistUrl, { videoType })
          .then(({ data }) => {
            const playlist = { file: {}, drm: {} };

            // Parse sources
            Object.entries(data.sources).forEach(([sourceType, source]) => {
              playlist.file[sourceType.toLowerCase()] = source[0].src;
            });

            // Parse drm
            Object.entries(data.drm).forEach(([drmType, drm]) => {
              const drmSrc = rewriteUrl(drm.src);

              playlist.drm[drmType.toLowerCase()] = drmType === 'FAIRPLAY'
                ? { src: drmSrc, cert: drm.cert }
                : drmSrc;
            });

            return playlist;
          });
      },
      checkDashManifest(playlist) {
        if (!playlist.file.dash) {
          return Promise.reject();
        }

        return axios.head(playlist.file.dash);
      },
      async setup({ videoType, config = {}, playlist = {} }) {
        this.config = config;
        const settings = state.settings;
        const { audio, profile, subtitle } = settings;
        const audioLang = getTrackLanguage(audio);
        const subtitleLang = subtitle.language;
        const product = this.product;
        const autoAdaptation = isNaN(profile.bandwidth);
        const options = {
          ...playlist,
          id: product.id,
          width: '100%',
          height: '100%',
          volumeLocalStorageEnabled: false,
          pg: 18,
          controls: false,
          disableNativeControls: true,
          fairplayContentType: 'application/octet-stream',
          autoplayBlockedRule: 'mute',
          seekPrecision: 'time',
          reconnectLiveOnSegmentFailed: false,
          liveDelay: this.info.platformSettings.liveDelay || 50,
          ...(audioLang && { defaultAudioLanguage: audioLang }),
          autoAdaptation,
          defaultBitrate: autoAdaptation ? 1 : profile.bandwidth / 1000,
          ...(subtitleLang && subtitleLang !== SubtitlesLanguage.DISABLED && {
            defaultSubtitle: subtitleLang,
            subtitleEnabled: true,
          }),
          updateManifestWithResponseUrl: true,
          drmSystems: this.info.platformSettings?.drmSystems || DEFAULT_DRM_SYSTEMS,
        };

        if (product.previewUrl && ![VideoType.LIVE, VideoType.CATCHUP].includes(videoType)) {
          axios.get(product.previewUrl)
            .then(({ data }) => {
              setSpriteManifest(data);
              setSpriteBaseUrl(product.previewUrl.replace('preview.json', ''));
            });
        }

        if (config.tracking) {
          options.tracking = config.tracking;
        }

        if (videoType === VideoType.LIVE) {
          if (config.timeShift) {
            setTimeshiftMode(TimeshiftMode.LIVE);

            options.timeShiftParams = {
              startTime: 'begin',
              stopTime: 'end',
            };

            options.timeShift = {
              unixTimestamp: true,
              start: config.timeShift.start,
              play: this.$storage.playFromStart && this.product.currentProgram.since,
            };
          } else {
            setTimeshiftMode(TimeshiftMode.DISABLED);
          }

          this.$storage.playFromStart = false;
        }

        const bookmark = this.bookmarks.get(this.getProductId());
        options.startTime = await this.getStartTime({
          bookmark,
          videoType,
        });

        this.player = await this.$redGalaxyPlayer.createInstance(this.$refs.video, this.abortController.signal);

        if (!this.player) {
          return;
        }

        if (this.areAdsEnabled) {
          const { freeWheelInfo } = this.info;
          const { freewheelServerUrl, networkId, profile: adsProfile, siteSectionId } = freeWheelInfo;
          const disabledAdvertTypes = [];

          if (options.startTime && ![VideoType.LIVE, VideoType.CATCHUP].includes(this.videoType)) {
            disabledAdvertTypes.push(advertType.PREROLL);
          }

          if (this.videoType === VideoType.LIVE) {
            disabledAdvertTypes.push(advertType.MIDROLL);
          }

          const currentProduct = this.product.currentProgram || this.product;
          this.freeWheelPlayer = await this.$freewheel.createInstance({
            getStatsCurrentTime: this.getStatsCurrentTime,
            minBufferingTime: this.info.platformSettings?.minBufferingTimeMilliseconds,
            product: currentProduct,
            stats: this.$stats,
            video: this.$refs.video,
            assetDuration: currentProduct.duration,
            assetId: currentProduct.id,
            breaks: currentProduct.adBreak,
            customKeys: [],
            network: networkId,
            disabledAdvertTypes,
            profile: adsProfile,
            siteSection: this.getSiteSection(siteSectionId),
            url: freewheelServerUrl,
            $storage: this.$storage,
            $window: this.$window,
          });
        }

        return this.$nextTick()
          .then(() => this.player.setup(options))
          .then(() => this.freeWheelPlayer?.init())
          .then(() => this.$redGalaxyPlayer.checkAutoplay())
          .then(({ isAutoplayAllowed, isMutedAutoplayAllowed }) => {
            if (!isAutoplayAllowed && !isMutedAutoplayAllowed) {
              setAutoplayStatus(AutoplayStatus.BLOCKED);
              return;
            }

            if (!isAutoplayAllowed && isMutedAutoplayAllowed) {
              setAutoplayStatus(AutoplayStatus.MUTED_ALLOWED);
              setMuted(true);
            }

            this.loadPlayer();
          });
      },
      loadPlayer() {
        const volume = state.mute ? MIN_VOLUME : this.$storage.volume || MAX_VOLUME;

        if (!this.freeWheelPlayer) {
          this.player.on('ended', this.onEnded);

          setPlayerStatus(PlayerStatus.LOADING);
          return this.player.load().then(() => {
            this.updateVolume();
            setIdle();
          });
        }

        setAdvertControlsEnabled(true);
        this.freeWheelPlayer.setVolume(volume);
        this.freeWheelPlayer.on('playContent', () => {
          setPlayerStatus(PlayerStatus.LOADING);
          this.player.load().then(() => {
            this.updateVolume();
            setIdle();
          });
        });
        this.freeWheelPlayer.on('videoAutoPlayBlocked', () => {
          setMuted(true);
          this.freeWheelPlayer.mute();
          this.freeWheelPlayer.play();
        });
        this.freeWheelPlayer.on('ended', () => this.onEnded());
        this.freeWheelPlayer.on('onSlotStarted', () => {
          setPlayerStatus(PlayerStatus.ADVERTS_PLAYING);
        });
        this.freeWheelPlayer.on('midrollEnded', () => {
          if (this.saveBookmarkAfterAds) {
            this.saveBookmark();
            this.saveBookmarkAfterAds = false;
          }
        });

        setPlayerStatus(PlayerStatus.ADVERTS_BUFFERING);
        this.freeWheelPlayer.load();
      },
      hideCountdownAndStartPlayer() {
        this.isCountdownVisible = false;
        this.start();
      },
      getConfigurationHeaders() {
        if (!window.__ipGetIdAn || !window.__ipGetId) {
          return {};
        }

        const ipId = (window.__ipGetIdAn() && 'A:' + window.__ipGetIdAn()) || window.__ipGetId();
        return { [HeaderName.IPRESSO_CONTACT_HASH]: ipId };
      },
      getSiteSection(siteSectionId) {
        const label = getMainCategoryLabel(this.product).toLowerCase();
        const deviceType = getDeviceType();
        const location = getLocation();

        return siteSectionId
          .replace('$devicetype$', deviceType)
          .replace('$label$', label)
          .replace('$referrer$', location.hostname);
      },
      getStartTime({ bookmark, videoType }) {
        const playTime = bookmark && bookmark.bookmark
          && !bookmark.bookmark.watched
          && bookmark.bookmark.playTime;

        if (playTime && BookmarkVideoType[videoType]) {
          this.bookmark = formatTime(playTime);
          return playTime;
        }

        return Promise.resolve(0);
      },
      getPlayPromise() {
        return this.playPromise || Promise.resolve();
      },
      goToEmbeddedRecommendations() {
        if (this.product.recommendationsDisabled) {
          setOverlayStatus(OverlayStatus.INTERACTION);
          setPlayerStatus(PlayerStatus.WAITING_INTERACTION);
          this.$emit('replay');
          return;
        }

        this.$router.replace({
          name: EmbeddedRouteName.PLAYER,
          params: {
            slug: 'recommendations',
            type: this.product.type || ProductType.VOD,
            id: this.product.id || 0,
            isFollowingElement: true,
          },
          query: this.$route.query,
        });
      },
      fullscreenChange() {
        const wrapper = document.querySelector('.js-player-wrapper');
        const video = document.querySelector('.js-player-video');

        fullscreenChange(video, wrapper);
      },
      onTrackingError() {
        this.showAlert(ErrorCode.TRACKING_SCRIPT_ERROR);
        this.getPlayPromise().finally(() => this.close());
      },
      handleMouseMove() {
        setOverlayStatus(OverlayStatus.INTERACTION);
        setIdle();
      },
      close() {
        if (this.player) {
          this.$stats.stopPlaying(this.product, this.getStatsCurrentTime());

          if (!this.durationExceeded) {
            this.$stats.closePlayer(this.product, this.getStatsCurrentTime());
          }
        }
        this.durationExceeded = false;
        this.destroy();
      },
      onDurationExceeded() {
        if (!this.durationExceeded) {
          this.durationExceeded = true;
          this.freeWheelPlayer ? this.freeWheelPlayer.onContentVideoEnded() : this.onEnded();
        }
      },
      async onEnded() {
        this.$chartbeat.destroy();
        this.durationExceeded = true;
        this.$stats.complete(this.product, this.getStatsCurrentTime());

        if (this.nextEpisode.id) {
          this.playNextEpisode();
        } else if (this.isEmbedded) {
          this.recommendationAutoplay ? await this.goToComingNext() : this.goToEmbeddedRecommendations();
        } else if (this.nextProgram.id) {
          this.playNextProgram();
        } else if (this.nextRelatedProduct.id) {
          this.playNextRelatedProduct();
        } else {
          await this.goToComingNext();
        }
      },
      async goToComingNext() {
        const { id = 0, type = ProductType.VOD } = this.product;
        const recommendations = new ProductRecommendations({ id, type });

        await recommendations.fetchAll('', { params: { maxResults: 1 } });
        this.comingNextProduct = (recommendations?.collection.records || [])[0];
        this.comingNextMessage = this.comingNextProduct ? '' : this.$t('player.comingNext.acknowledgement');
        this.comingNextVisible = true;
      },
      destroy() {
        this.stopSession();

        this.debounceBookmark?.cancel();
        this.throttleBookmark?.cancel();
        this.throttleAdblock?.cancel();
        this.throttleCurrentTime?.cancel();
        this.throttleUpdateGradualPercentProgress?.cancel();

        if (this.player) {
          this.saveBookmark();
          this.player.destroy();
          this.player = undefined;
        }

        this.abortController?.abort();
        this.abortController = undefined;
        this.freeWheelPlayer?.destroy();
        this.$chartbeat.destroy();
        setInitState();
      },
      prolongSession() {
        if (this.config.prolongVideoSessionUrl) {
          SDK.apiAdapter.put(this.config.prolongVideoSessionUrl).catch((error) => {
            const response = error && error.response;
            const status = (response && response.status) || 500;

            if (status >= 500) {
              const now = new Date().getTime();

              if (this.lastSessionError === 0) {
                this.lastSessionError = now;
              }

              const errorInterruptTime = (now - this.lastSessionError) / 1000;

              if (errorInterruptTime < this.config.errorInterruptTime || ERROR_INTERRUPT_TIME) {
                return;
              }
            }

            this.lastSessionError = 0;

            let code = '';
            if (response) {
              code = response.code || response.data.code;
            }

            if (code === ErrorCode.VIDEO_SESSION_EXPIRED) {
              this.$stats.errorWhilePlaying(this.product, this.getStatsCurrentTime(), code);
            }

            this.showAlert(code);
            this.destroy();
          });
        }
      },
      stopSession() {
        this.throttleSession?.cancel();

        if (this.config && this.config.prolongVideoSessionUrl) {
          SDK.apiAdapter.del(this.config.prolongVideoSessionUrl);
        }
      },
      updateVolume() {
        if (!this.player) {
          return;
        }

        const mute = state.mute;
        const volume = mute
          ? MIN_VOLUME
          : this.$storage.volume || MAX_VOLUME;
        this.player.setVolume(volume);

        if (mute) {
          this.player.mute();
        } else {
          this.player.unMute();
        }
      },
      saveBookmark() {
        if (state.isLinearAdPlaying) {
          this.saveBookmarkAfterAds = true;
          return;
        }

        const canCreateBookmark = !!(this.player && this.subscriber.isLogged && BookmarkVideoType[this.videoType]);
        if (!canCreateBookmark) {
          return;
        }

        const { bookmarksIntervalInSeconds } = this.info.platformSettings;
        const { minTime, percentageWatched } = this.info.bookmarkInfo;
        const currentTime = parseInt(this.player.getCurrentTime(), 10);
        const shouldCreateBookmark = !!(currentTime >= minTime
          && (!this.savedPosition || (this.savedPosition && Math.abs(currentTime - this.savedPosition) >= parseInt(bookmarksIntervalInSeconds, 10)))
        );

        if (!shouldCreateBookmark) {
          return;
        }

        const { duration } = this.product;
        const { creditsTime } = this.config;
        const progress = Math.floor((currentTime / duration) * 100);
        const watched = creditsTime ? currentTime >= creditsTime : progress >= percentageWatched;

        this.savedPosition = currentTime;
        this.bookmarks.create(this.product, {
          itemId: this.getProductId(),
          playTime: this.savedPosition,
          watched,
        });
      },
      deleteBookmark() {
        if (this.subscriber.isLogged
          && this.videoType !== VideoType.LIVE) {
          this.bookmarks.remove(this.product, {
            sync: true,
          });
        }
      },
      getTracking({ tracking }) {
        if (tracking) {
          return {
            collector: tracking.url,
            sessionId: tracking.sessionId,
            customerId: tracking.customerId,
            subscriberId: tracking.subscriberId,
            productId: this.id,
            contentId: tracking.contentId,
            customData: tracking.customData,
          };
        }

        return tracking;
      },
      showAlert(type) {
        if (this.$refs['alert-generic']) {
          this.$refs['alert-generic'].show(type);
        }
      },
      showError(error) {
        setPlayerStatus(PlayerStatus.ERROR);
        setControlsEnabled(false);
        this.showAlert(error.code || error);
      },
      hideAlert() {
        if (this.$refs['alert-generic']) {
          this.$refs['alert-generic'].hide();
        }
      },
      getProductId() {
        if (this.videoType === VideoType.CATCHUP) {
          return this.product.id;
        }

        if (this.videoType === VideoType.RECORDING) {
          return this.product.programmeId;
        }

        return this.id;
      },
      getAvailableSince() {
        if (this.videoType === VideoType.LIVE) {
          const isFuture = this.time.isFutureDate(this.product.currentProgram);

          if (!isFuture) {
            return;
          }

          return this.product.currentProgram.since;
        }

        const actualSoonSchedule = getActiveScheduleByType(this.product, DisplayScheduleType.SOON);
        if (actualSoonSchedule) {
          return dayjs(actualSoonSchedule.till).valueOf();
        }
      },
      onHevcError() {
        this.hevcPlayback = false;
        this.player.load(this.originalPlaylist.file);
      },
    },
  };
</script>
